import * as React from 'react'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import { ChevronDown } from 'react-feather'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect } from 'react'
import Permission from 'src/services/PermissionService'

interface DropdownButtonProps {
  options: any
  handleClick: (index: number, value: string) => void
  variant?: 'contained' | 'outlined'
  title?: string
  selectedOption?: number
  size?: 'small' | 'large'
  fullWidth?: boolean
  renderOnlyIcon?: boolean
  disabled?: boolean
  tooltipTitle?: string
  disablePortal?: boolean
  popperConfig?: {
    maxHeight?: string
  }
  disableSelectedOption?: boolean
}

const styles = {
  root: {
    '&:disabled': {
      border: '1px solid #f0f0f0',
      backgroundColor: 'white'
    }
  }
}

const TriggerButton = withStyles(styles)(Button)

const DropdownButton: React.FC<DropdownButtonProps> = ({
  options,
  handleClick,
  variant = 'contained',
  title,
  selectedOption,
  size = 'small',
  fullWidth = false,
  renderOnlyIcon = false,
  disabled = false,
  tooltipTitle = '',
  disablePortal = true,
  popperConfig,
  disableSelectedOption = false
}) => {
  // const classes = useStyles()
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = React.useState(0)

  useEffect(() => {
    if (typeof selectedOption !== 'undefined') {
      setSelectedIndex(selectedOption)
    }
  }, [selectedOption])

  useEffect(() => {
    if (typeof options !== 'undefined' && options?.length) {
      !Boolean(options?.[selectedIndex]) && setSelectedIndex(0)
    }
  }, [options])

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number, value: string) => {
    if (!disableSelectedOption) {
      setSelectedIndex(index)
    }
    handleClick(index, value)
    setOpen(false)
  }

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (event: Event) => {
    event.stopPropagation()
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <div
          ref={anchorRef}
          aria-label='split button'
          className={`h-9 ${fullWidth ? 'w-full' : 'w-fit'} ${disabled ? 'cursor-not-allowed' : ''}`}
        >
          <TriggerButton
            variant={variant}
            color={variant !== 'contained' ? 'secondary' : 'primary'}
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label='Select create mode'
            aria-haspopup='menu'
            fullWidth={fullWidth}
            onClick={handleToggle}
            className={`flex gap-1 items-center text-sm justify-between shadow-sm !border-2 !border-solid !border-gray-light bg-white ${
              size === 'small' ? 'p-2 h-8' : 'p-3 h-9'
            } ${disabled ? 'disabled' : ''}`}
            disabled={disabled}
          >
            {title && (
              <span className={`font-normal  ${!disabled ? 'text-[#4d4d4d]' : 'text-gray-dark'}`}>{title}</span>
            )}
            <span
              className={`text-coal-dark font-semibold ${size === 'small' ? 'text-xs' : 'text-sm'} ${
                !disabled ? 'text-[#4d4d4d]' : 'text-gray-dark'
              }`}
            >
              {' '}
              {variant === 'contained' ? (
                <>
                  {renderOnlyIcon && options?.[0]?.icon && React.createElement(options?.[0]?.icon)}{' '}
                  {options?.[0]?.label}
                </>
              ) : (
                <>
                  {renderOnlyIcon &&
                    options?.[selectedIndex]?.icon &&
                    React.createElement(options?.[selectedIndex]?.icon)}{' '}
                  {options?.[selectedIndex]?.label}
                </>
              )}{' '}
            </span>
            <ChevronDown className='w-4 h-4 text-coal-dark ml-auto' />
          </TriggerButton>
        </div>
      </Tooltip>
      <Popper
        sx={{
          zIndex: 99990
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement='bottom-end'
        disablePortal={disablePortal ?? true}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              minWidth: '100px',
              width: '100%',
              transformOrigin: placement === 'bottom' ? 'right end' : 'right start'
            }}
          >
            <Paper sx={{ borderRadius: 2, maxHeight: popperConfig?.maxHeight, overflow: 'auto' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu' autoFocusItem>
                  {options?.map((option: any, index: number) => (
                    <Permission
                      permissionId={option.permissionId}
                      key={`${index}_${option.permissionId}`}
                      customClass='flex justify-center items-center w-full'
                    >
                      <MenuItem
                        disabled={option?.disabled}
                        key={option?.value}
                        selected={index === selectedIndex}
                        onClick={event => {
                          event.stopPropagation()
                          handleMenuItemClick(event, index, option?.value)
                        }}
                        sx={
                          renderOnlyIcon
                            ? {
                                justifyContent: 'center'
                              }
                            : {}
                        }
                      >
                        {option.icon && <option.icon />}
                        <Typography variant={'subtitle2'}>{option.label}</Typography>
                      </MenuItem>
                    </Permission>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default DropdownButton
